import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { MDSCard, MDSCardVariant, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";

export type AgentMetaItem = {
  label: string;
  value: ReactNode;
  wide?: boolean;
};

export type AgentMetaSection = {
  description?: string;
  items: AgentMetaItem[];
  title?: string;
};

export const AgentMetaCard = ({
  items,
  sections,
  title,
}: {
  items?: AgentMetaItem[];
  sections?: AgentMetaSection[];
  title?: string;
}) => (
  <StyledCard variant={MDSCardVariant.Chrome}>
    {title ? (
      <MDSText color={MDSTextColor.Primary} variant={MDSTextVariant.HeaderS}>
        {title}
      </MDSText>
    ) : null}
    {(sections ?? [{ items: items ?? [] }]).map((section, index) => (
      <StyledSection key={section.title ?? index}>
        {section.title ? (
          <StyledSectionHeader>
            <MDSText color={MDSTextColor.Primary} variant={MDSTextVariant.TextS}>
              {section.title}
            </MDSText>
            {section.description ? (
              <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                {section.description}
              </MDSText>
            ) : null}
          </StyledSectionHeader>
        ) : null}
        <StyledGrid>
          {section.items.map(item => (
            <StyledItem key={item.label} $wide={item.wide ?? false}>
              <StyledLabel>
                <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                  {item.label}
                </MDSText>
              </StyledLabel>
              <StyledValue>
                {typeof item.value === "string" ? (
                  <MDSText color={MDSTextColor.Primary} variant={MDSTextVariant.TextS}>
                    {item.value}
                  </MDSText>
                ) : (
                  item.value
                )}
              </StyledValue>
            </StyledItem>
          ))}
        </StyledGrid>
      </StyledSection>
    ))}
  </StyledCard>
);

const StyledCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  min-width: 0;
`;

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
    padding-top: ${({ theme }) => theme.spacing[4]};
  }
`;

const StyledSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledGrid = styled.dl`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[6]};
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin: 0;
`;

const StyledItem = styled.div<{ $wide: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  grid-column: ${({ $wide }) => ($wide ? "span 2" : "auto")};
  min-width: 0;

  @media (max-width: 520px) {
    grid-column: auto;
  }
`;

const StyledLabel = styled.dt`
  margin: 0;
  min-width: 0;
`;

const StyledValue = styled.dd`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
`;
